import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  CreateFamilyProfileRequest,
  FamilyContextProfile,
  FamilyContextResponse,
} from '@nexus/api-types';
import { catchError, map, of, shareReplay, startWith, type Observable } from 'rxjs';
import { mapWishlistRequestError } from '../errors/map-wishlist-request-error';
import type { WishlistRequestState } from '../types/wishlist-request-state.type';

@Injectable({
  providedIn: 'root',
})
export class WishlistBeneficiaryProfilesService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly apiBaseUrl: string = '/api/v1/family';

  public getFamilyContext(): Observable<WishlistRequestState<FamilyContextResponse>> {
    return this.toRequestState(this.httpClient.get<FamilyContextResponse>(`${this.apiBaseUrl}/context`));
  }

  public createProfile(
    request: CreateFamilyProfileRequest,
  ): Observable<WishlistRequestState<FamilyContextProfile>> {
    return this.toRequestState(
      this.httpClient.post<FamilyContextProfile>(`${this.apiBaseUrl}/profiles`, request),
    );
  }

  private toRequestState<TData>(source: Observable<TData>): Observable<WishlistRequestState<TData>> {
    return source.pipe(
      map((data: TData): WishlistRequestState<TData> => createSuccessState(data)),
      startWith(createLoadingState<TData>()),
      catchError((error: unknown): Observable<WishlistRequestState<TData>> => of(createErrorState<TData>(error))),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}

function createLoadingState<TData>(): WishlistRequestState<TData> {
  return {
    status: 'loading',
    data: null,
    error: null,
  };
}

function createSuccessState<TData>(data: TData): WishlistRequestState<TData> {
  return {
    status: 'success',
    data,
    error: null,
  };
}

function createErrorState<TData>(error: unknown): WishlistRequestState<TData> {
  return {
    status: 'error',
    data: null,
    error: mapWishlistRequestError(error),
  };
}
